import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { DashboardNavGroup } from '../navigation';
import type { DashboardTranslations } from '../translations';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  groups: DashboardNavGroup[];
  t: DashboardTranslations;
}

export function CommandPalette({ open, onClose, groups, t }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const commands = useMemo(
    () =>
      groups.flatMap((group) =>
        group.items.map((item) => ({
          ...item,
          groupKey: group.key,
          label: t.nav.items[item.key],
          description: t.nav.descriptions[item.key],
          groupLabel: t.nav.groups[group.key],
        })),
      ),
    [groups, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.description} ${c.groupLabel} ${c.to}`.toLocaleLowerCase().includes(q),
    );
  }, [commands, query]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === 'Enter' && filtered[activeIndex]) {
        event.preventDefault();
        navigate(filtered[activeIndex].to);
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, filtered, navigate, onClose, open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  return (
    <LazyMotion features={domAnimation} strict>
      <AnimatePresence>
        {open ? (
          <m.div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 px-4 pt-24 backdrop-blur-xl sm:pt-28"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cmd-title"
          >
            <m.div
              className="w-full max-w-xl overflow-hidden rounded-2xl bg-surface-card border border-surface-border shadow-lift"
              initial={{ scale: 0.95, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 6 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <div className="flex items-start justify-between gap-4 px-4 pt-4">
                <div>
                  <h2 id="cmd-title" className="font-display text-display-md text-brand-white">
                    {t.command.title}
                  </h2>
                  <p className="mt-1 text-sm text-brand-muted">{t.command.description}</p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex size-9 items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
                  aria-label={t.command.closeAria}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="px-4 pt-4 pb-2">
                <label className="flex min-h-12 items-center gap-3 rounded-xl bg-surface-base border border-surface-border px-4 text-brand-muted transition-colors">
                  <Search size={16} />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t.command.placeholder}
                    className="h-12 w-full bg-transparent text-sm text-brand-white placeholder:text-brand-muted/60 outline-none"
                  />
                </label>
              </div>

              <div className="max-h-[44vh] overflow-y-auto p-2" aria-label={t.command.resultLabel}>
                {filtered.length > 0 ? (
                  <div className="space-y-1">
                    {filtered.map((cmd, index) => {
                      const Icon = cmd.icon;
                      return (
                        <button
                          key={`${cmd.groupKey}-${cmd.key}`}
                          type="button"
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => {
                            navigate(cmd.to);
                            onClose();
                          }}
                          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start transition-colors ${
                            index === activeIndex
                              ? 'bg-accent/10 text-brand-white'
                              : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                          }`}
                        >
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-hover border border-surface-border text-accent">
                            <Icon size={16} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold">{cmd.label}</span>
                            <span className="mt-0.5 block truncate text-xs text-brand-muted">
                              {cmd.description}
                            </span>
                          </span>
                          <span className="hidden rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-widest text-brand-muted sm:inline-flex">
                            {cmd.groupLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="px-3 py-8 text-center text-sm text-brand-muted">{t.command.empty}</p>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-surface-border px-4 py-3 text-xs text-brand-muted">
                <span>{t.command.hintNavigate}: Enter</span>
                <span>{t.command.hintClose}: Esc</span>
              </div>
            </m.div>
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  );
}
